import { Observer, observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import {
  Popover,
  Menu,
  MenuDivider,
  MenuItem,
  Button,
  Spinner,
  useToast,
} from '../../npaper';
import { VatRateSelect } from '../../npaper/VatRateSelect';
import { ISaleItem, ProductType, useStores } from '../../stores';
import { TaskSideSheet } from '../tasks/TaskSideSheet';

export interface ISaleItemActionMenuProps {
  onDeleteLine: () => void;
  saleItem: ISaleItem;
  canChangeUnitPrice: boolean;
  canAddItems: boolean;
  showStatus: boolean;
}

export const SaleItemActionMenu: React.FC<ISaleItemActionMenuProps> = observer(
  ({ onDeleteLine, saleItem, canAddItems, canChangeUnitPrice, showStatus }) => {
    const toaster = useToast();
    const intl = useIntl();
    const history = useHistory();
    const { saleItemStore, saleStore, agreementStore, taskStore } = useStores();
    const [inspectingTask, setInspectingTask] = useState(false);

    const refreshSaleOrAgreement = async () => {
      const snapshot = getSnapshot(saleItem);
      if (snapshot.sale) await saleStore.getItem(snapshot.sale, true);
      if (snapshot.agreement)
        await agreementStore.getItem(snapshot.agreement, true);
    };

    return (
      <>
        {saleItem.task && (
          <TaskSideSheet
            isShown={inspectingTask}
            onClose={() => setInspectingTask(false)}
            task={saleItem.task}
          />
        )}

        <Popover
          placement="bottom-end"
          onOpen={() => {
            if (
              saleItem.product !== null &&
              (!saleItem.related_purchase_orders ||
                saleItem.related_purchase_orders.length === 0)
            ) {
              saleItemStore.getItem(saleItem.id, true);
            }
          }}
          content={(close) => (
            <Observer>
              {() => (
                <Menu>
                  {!saleItem.is_hidden &&
                    saleItem.parent_item === null &&
                    !saleItem.is_heading && (
                      <>
                        <MenuDivider
                          text={intl.formatMessage({ defaultMessage: 'VAT' })}
                        />
                        <VatRateSelect
                          placement={'left-start'}
                          disabled={!canChangeUnitPrice}
                          onSelect={(rate) => {
                            saleItemStore
                              .updateItem(saleItem, {
                                explicit_vat_rate: rate.id,
                              } as any)
                              .then(() => refreshSaleOrAgreement());
                            close();
                          }}
                          selected={getSnapshot(saleItem).vat_rate || undefined}
                        >
                          <MenuItem
                            text={
                              saleItem.vat_rate ? saleItem.vat_rate.code : ''
                            }
                          />
                        </VatRateSelect>
                      </>
                    )}
                  <MenuDivider
                    text={intl.formatMessage({ defaultMessage: 'Actions' })}
                  />
                  {showStatus && (
                    <>
                      {/*saleItem.product &&
                        saleItem.product.type === ProductType.GOOD && (
                          <MenuItem
                            icon="flag"
                            text={intl.formatMessage({
                              defaultMessage: 'Mark as complete',
                            })}
                            onClick={() => {
                              close();
                              alert('TODO!!');
                            }}
                          />
                          )*/}
                      {saleItem.product &&
                        saleItem.product.type === ProductType.SERVICE &&
                        (saleItem.task ? (
                          <MenuItem
                            icon="build"
                            text={intl.formatMessage({
                              defaultMessage: 'Show task',
                            })}
                            onClick={() => {
                              close();
                              setInspectingTask(true);
                            }}
                          />
                        ) : (
                          <MenuItem
                            icon="add"
                            text={intl.formatMessage({
                              defaultMessage: 'Create task',
                            })}
                            onClick={() =>
                              taskStore.createItem({
                                sale_item: saleItem.id,
                              } as any)
                            }
                          />
                        ))}
                    </>
                  )}
                  {saleItem.child_items && saleItem.child_items.length > 0 && (
                    <MenuItem
                      icon="ungroup-objects"
                      text={intl.formatMessage({
                        defaultMessage: 'Ungroup items',
                      })}
                      onClick={async () => {
                        close();
                        try {
                          await saleItemStore.updateItem(saleItem, {
                            child_items: [],
                          });
                          await saleItemStore.deleteItem(saleItem);
                          refreshSaleOrAgreement();
                        } catch (err) {
                          toaster.showDefaultError(err);
                        }
                      }}
                    />
                  )}
                  {saleItem.related_purchase_orders.map((purchaseOrder, i) =>
                    purchaseOrder ? (
                      <MenuItem
                        key={purchaseOrder.id}
                        icon="truck"
                        text={intl.formatMessage(
                          { defaultMessage: 'Show purchase order {number}' },
                          { number: purchaseOrder.purchase_order_number }
                        )}
                        onClick={() => {
                          close();
                          history.push(`/purchase-orders/${purchaseOrder.id}`);
                        }}
                      />
                    ) : (
                      <MenuItem
                        key={`index-${i}`}
                        text={<Spinner size={16} />}
                      />
                    )
                  )}
                  {canAddItems && (
                    <>
                      <MenuDivider />
                      <MenuItem
                        icon="trash"
                        intent="danger"
                        text={intl.formatMessage({
                          defaultMessage: 'Delete line',
                        })}
                        onClick={() => {
                          close();
                          onDeleteLine();
                        }}
                      />
                    </>
                  )}
                </Menu>
              )}
            </Observer>
          )}
        >
          <Button iconLeft="more" minimal />
        </Popover>
      </>
    );
  }
);
